import { EPaymentPlan, EWorksheetDecision } from '@if/core/types'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { IfXpressWorksheet, IfXpressWorksheetRevision } from '@src/data/types/IfXpressWorksheetSchema'
import translate from '@src/services/translate'
import type { TRootState } from '..'
import { NormsMessage } from '../../types'
import { ParameterizedMessage } from '../../types/ParameterizedMessage'
import { apiStateSelectors } from '../ApiStateStore'
import { appSelectors } from '../AppStore'

export const IfXpressWorksheetSlice = createSlice({
  name: 'ifXpressWorksheet',
  initialState: {
    current: null as IfXpressWorksheet | null,
    computedInfo: {} as FundingComputedDto,
    currentRevisions: [] as IfXpressWorksheetRevision[],
  },
  reducers: {
    setCurrent: (state, action: PayloadAction<IfXpressWorksheet | null>) => {
      state.current = action.payload
    },
    setFundingComputed: (state, action: PayloadAction<FundingComputedDto | null>) => {
      if (action.payload)
        state.computedInfo = {
          ...state.computedInfo,
          ...action.payload,
        }
    },
    setCurrentRevisions: (state, action: PayloadAction<IfXpressWorksheetRevision[]>) => {
      state.currentRevisions = action.payload.sort((a, b) => {
        if (!a.updatedOn) return -1
        if (!b.updatedOn) return 1
        return a.updatedOn < b.updatedOn ? 1 : -1
      })
    },
  },
})

export default IfXpressWorksheetSlice.reducer

export const ifXpressWorksheetActions = IfXpressWorksheetSlice.actions

export const SAVE_IFXPRESS_WORKSHEET = 'ifXpressWorksheet/save'
export const COMPUTE_IFXPRESS_FUNDING = 'ifXpressWorksheet/computeIfXpressFunding'
export const GET_IFXPRESS_WORKSHEET_REVISIONS = 'ifXpressWorksheet/revisions'
export const SAVE_IFXPRESS_WORKSHEET_DECISION = 'ifXpressWorksheet/saveDecision'
export const GET_IFXPRESS_WORKSHEET_BY_ID = 'ifXpressWorksheet/getIfXpressWorksheetById'

const ifXpressWorksheetSelectorsPartial = {
  isSavingIfXpressWorksheet: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_IFXPRESS_WORKSHEET),
  isComputeIfXpressFunding: (state: TRootState) => apiStateSelectors.isLoading(state, COMPUTE_IFXPRESS_FUNDING),
  getCurrent: (state: TRootState) => state.ifXpressWorksheet.current,
  getCurrentRevisions: (state: TRootState) => state.ifXpressWorksheet.currentRevisions,
  getComputedInfo: (state: TRootState) => state.ifXpressWorksheet.computedInfo,
  getTotalfees: (state: TRootState) => state.ifXpressWorksheet.computedInfo?.lenderFee || 0,
  getTotalInsurance: (state: TRootState) =>
    (state.ifXpressWorksheet.computedInfo?.insuranceFee || 0) +
    (state.ifXpressWorksheet.computedInfo?.insuranceTax || 0),
  getTotalObligation: (state: TRootState) =>
    (state.ifXpressWorksheet.current?.amountRequested || 0) +
    (state.ifXpressWorksheet.computedInfo?.totalInterestAmount || 0) +
    (state.ifXpressWorksheet.computedInfo?.lenderFee || 0) +
    (state.ifXpressWorksheet.computedInfo?.insuranceFee || 0) +
    (state.ifXpressWorksheet.computedInfo?.insuranceTax || 0),
  worksheetIsApproved: (state: TRootState) =>
    state.ifXpressWorksheet.current?.finalDecision.decision === EWorksheetDecision.Approved,
  hasConfirmedPromotion: (state: TRootState) =>
    state.ifXpressWorksheet.current !== null &&
    state.ifXpressWorksheet.current?.paymentPlanId !== EPaymentPlan.RegularDailyInterests,
  isCreated: (state: TRootState) => state.ifXpressWorksheet.current !== null,
}

function getParameterizedNormMessage(message: ParameterizedMessage): string {
  const translatedMessage = translate([`worksheet.norms.${message.id}`])

  let messageSuffix = ' | '
  message.parameters.forEach((p, i) => {
    if (i >= 1) messageSuffix = messageSuffix.concat(', ')

    const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
    messageSuffix = messageSuffix.concat(suffix)
  })

  return translatedMessage.concat(messageSuffix)
}

const getCurrentWorksheetNormWarningMessages = createSelector(
  [ifXpressWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const worksheetNormMessages: NormsMessage[] = []
    currentWorksheet?.normsDecision?.warningMessages.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })

    return worksheetNormMessages
  },
)

const getCurrentWorksheetNormRefusalMessages = createSelector(
  [ifXpressWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const worksheetNormMessages: NormsMessage[] = []
    currentWorksheet?.normsDecision?.refusalMessages.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })

    return worksheetNormMessages
  },
)
const getCurrentWorksheetNormRefusalAndWarningMessages = createSelector(
  [ifXpressWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const warningsAndRefusals = currentWorksheet?.normsDecision?.warningMessages?.concat(
      currentWorksheet?.normsDecision?.refusalMessages,
    )

    const worksheetNormMessages: NormsMessage[] = []
    warningsAndRefusals?.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })
    return worksheetNormMessages
  },
)

export const ifXpressWorksheetSelectors = {
  ...ifXpressWorksheetSelectorsPartial,
  getCurrentWorksheetNormRefusalAndWarningMessages,
  getCurrentWorksheetNormWarningMessages,
  getCurrentWorksheetNormRefusalMessages,
}
